import axios from "axios";
import { API_DETAILS } from "../App";

/*
 * A patient's medical profile: preferred pharmacy, medications, allergies,
 * medical and surgical history, and the flagged medication classes they say they
 * take. Filled from the intake form, edited by the patient from their Profile,
 * read by staff on the chart (backend MedicalProfileController).
 *
 * The patient may read and change only their own; staff may read any. Medication
 * alerts are staff-only: the server refuses a patient token even for their own.
 */

const api = (path) => API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + path;

/** The four classes providers are alerted about, as the server names them. */
export const MEDICATION_CLASSES = [
  { id: "NITRATES", label: "Nitrates", examples: "for example nitroglycerin, isosorbide, Imdur" },
  { id: "DIGOXIN", label: "Digoxin", examples: "Lanoxin" },
  { id: "ANTICHOLINERGICS", label: "Anticholinergics", examples: "for example oxybutynin, tolterodine, Detrol, Vesicare" },
  { id: "NARCOTICS", label: "Narcotic pain medicine", examples: "for example oxycodone, hydrocodone, tramadol" },
];

export const EMPTY_PROFILE = {
  pharmacyName: "",
  pharmacyNpi: "",
  pharmacyAddress: "",
  pharmacyPhone: "",
  pharmacyFax: "",
  medications: "",
  allergies: "",
  medicalHistory: "",
  surgicalHistory: "",
  reportedMedicationClasses: null,
};

/** The server's nulls as empty strings, so the fields are controlled inputs. */
export function toForm(profile) {
  const out = { ...EMPTY_PROFILE };
  for (const key of Object.keys(EMPTY_PROFILE)) {
    if (key === "reportedMedicationClasses") {
      out[key] = Array.isArray(profile?.[key]) ? [...profile[key]] : null;
    } else if (profile?.[key] != null) {
      out[key] = String(profile[key]);
    }
  }
  return out;
}

/** Back to what PUT expects. Blank text goes as null ("not told us"). */
export function toRequest(form) {
  const out = {};
  for (const key of Object.keys(EMPTY_PROFILE)) {
    if (key === "reportedMedicationClasses") {
      // null leaves what is on file; the server reads [] as "none of these".
      out[key] = Array.isArray(form[key]) ? form[key] : null;
    } else {
      const v = (form[key] ?? "").trim();
      out[key] = v === "" ? null : v;
    }
  }
  return out;
}

/** One line for a chosen pharmacy: "261 CALHOUN ST, CHARLESTON, SC 29401". */
export function pharmacyAddressLine(p) {
  const cityLine = [p.city, [p.state, p.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [p.address, cityLine].filter(Boolean).join(", ");
}

/** A search result as profile fields. */
export function pharmacyToProfileFields(p) {
  return {
    pharmacyName: p.name || "",
    pharmacyNpi: p.npi || "",
    pharmacyAddress: pharmacyAddressLine(p),
    pharmacyPhone: p.phone || "",
    pharmacyFax: p.fax || "",
  };
}

export async function getMedicalProfile(patientId) {
  const res = await axios.get(api(`/patient/${patientId}/medical-profile`));
  return res.data;
}

export async function saveMedicalProfile(patientId, form) {
  const res = await axios.put(api(`/patient/${patientId}/medical-profile`), toRequest(form));
  return res.data;
}

/**
 * The pharmacy alone (backend PUT .../medical-profile/pharmacy). The intake form
 * uses this: the full-profile PUT would overwrite everything else on file.
 */
export async function saveMedicalPharmacy(patientId, pharmacy) {
  const f = pharmacyToProfileFields(pharmacy);
  const res = await axios.put(api(`/patient/${patientId}/medical-profile/pharmacy`), {
    name: f.pharmacyName || null,
    npi: f.pharmacyNpi || null,
    address: f.pharmacyAddress || null,
    phone: f.pharmacyPhone || null,
    fax: f.pharmacyFax || null,
  });
  return res.data;
}

/**
 * The intake answer for a pharmacy chosen from the search. It names the NPI so
 * the server, copying intake answers to the profile, recognises the pharmacy it
 * already saved and keeps its phone and fax (MedicalProfileService.applyIntake).
 */
export function pharmacyAnswerText(p) {
  const line = pharmacyAddressLine(p);
  return `${p.name}${line ? ", " + line : ""}${p.npi ? ` (NPI ${p.npi})` : ""}`;
}

/** Staff only. */
export async function getMedicationAlerts(patientId) {
  const res = await axios.get(api(`/patient/${patientId}/medication-alerts`));
  return res.data;
}

/** A pharmacy name (optional) and a 5-digit ZIP, or a city and two-letter state. */
export async function searchPharmacies({ name, zip, city, state }) {
  const params = {};
  if (name && name.trim()) params.name = name.trim();
  if (zip && zip.trim()) params.zip = zip.trim();
  if (city && city.trim()) params.city = city.trim();
  if (state && state.trim()) params.state = state.trim();
  const res = await axios.get(api("/pharmacies/search"), { params });
  return res.data;
}
